<script>
import { h, Comment, Fragment, Text } from "vue";

const breakpointValue = (mixed, windowWidth) => {
  const valueAsNum = parseInt(mixed);

  if (valueAsNum > -1) {
    return mixed;
  } else if (typeof mixed !== "object") {
    return 0;
  }

  let matchedBreakpoint = Infinity;
  let matchedValue = mixed.default || 0;

  Object.keys(mixed).forEach((key) => {
    const breakpoint = parseInt(key);
    const breakpointValRaw = mixed[breakpoint];
    const breakpointVal = parseInt(breakpointValRaw);

    if (Number.isNaN(breakpoint) || Number.isNaN(breakpointVal)) {
      return;
    }

    const isNewBreakpoint = windowWidth <= breakpoint && breakpoint < matchedBreakpoint;

    if (isNewBreakpoint) {
      matchedBreakpoint = breakpoint;
      matchedValue = breakpointValRaw;
    }
  });

  return matchedValue;
};

export default {
  name: "MasonryLayout",
  props: {
    tag: {
      type: [String],
      default: "div",
    },
    cols: {
      type: [Object, Number, String],
      default: 2,
    },
    gutter: {
      type: [Object, Number, String],
      default: 0,
    },
    css: {
      type: [Boolean],
      default: true,
    },
    columnTag: {
      type: [String],
      default: "div",
    },
    columnClass: {
      type: [String, Array, Object],
      default: () => [],
    },
    columnAttr: {
      type: [Object],
      default: () => ({}),
    },
  },
  data() {
    return {
      displayColumns: 2,
      displayGutter: 0,
      windowWidth: Infinity,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.reCalculate();
    });

    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.reCalculate);
    }
  },
  updated() {
    this.$nextTick(() => {
      this.reCalculate();
    });
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.reCalculate);
    }
  },
  methods: {
    reCalculate() {
      const previousWindowWidth = this.windowWidth;
      this.windowWidth = (typeof window !== "undefined" ? window.innerWidth : null) || Infinity;

      if (previousWindowWidth === this.windowWidth) {
        return;
      }

      this._reCalculateColumnCount(this.windowWidth);
      this._reCalculateGutterSize(this.windowWidth);
    },
    _reCalculateGutterSize(windowWidth) {
      this.displayGutter = breakpointValue(this.gutter, windowWidth);
    },
    _reCalculateColumnCount(windowWidth) {
      let newColumns = breakpointValue(this.cols, windowWidth);

      newColumns = Math.max(1, Number(newColumns) || 0);
      this.displayColumns = newColumns;
    },
    _flattenVNodes(vnodes, result = []) {
      if (!Array.isArray(vnodes)) return result;

      vnodes.forEach((vnode) => {
        if (!vnode) return;
        if (vnode.type === Comment || vnode.type === Text) return;

        if (vnode.type === Fragment && Array.isArray(vnode.children)) {
          this._flattenVNodes(vnode.children, result);
          return;
        }

        result.push(vnode);
      });

      return result;
    },
    _getChildItemsInColumnsArray() {
      const columns = [];
      const slotNodes = this.$slots.default ? this.$slots.default() : [];
      const childItems = this._flattenVNodes(slotNodes);

      for (let i = 0, visibleItemI = 0; i < childItems.length; i++, visibleItemI++) {
        const columnIndex = visibleItemI % this.displayColumns;

        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }

        columns[columnIndex].push(childItems[i]);
      }

      return columns;
    },
  },
  render() {
    const columnsContainingChildren = this._getChildItemsInColumnsArray();
    const isGutterSizeUnitless = parseInt(this.displayGutter) === this.displayGutter * 1;
    const gutterSizeWithUnit = isGutterSizeUnitless ? `${this.displayGutter}px` : this.displayGutter;

    const columnStyle = {
      boxSizing: "border-box",
      backgroundClip: "padding-box",
      width: `${100 / this.displayColumns}%`,
      border: "0 solid transparent",
      borderLeftWidth: gutterSizeWithUnit,
    };

    const columns = columnsContainingChildren.map((children, index) =>
      h(
        this.columnTag,
        {
          key: `${index}-${columnsContainingChildren.length}`,
          style: this.css ? columnStyle : null,
          class: this.columnClass,
          ...this.columnAttr,
        },
        children
      )
    );

    const containerStyle = {
      display: "flex",
      marginLeft: `-${gutterSizeWithUnit}`,
    };

    return h(this.tag, this.css ? { style: containerStyle } : null, columns);
  },
};
</script>
